import React from 'react';
import { Link } from 'react-router-dom';
import pluralize from 'pluralize';
import { useEventContext } from '../../../contexts/EventContext';
import { useV1EventForms } from '../../hooks/useV1EventForms';
import { useAccount } from '../../../../hooks/useAccount';
import { useEventDetails } from '../../../../hooks/useEventDetails';
import { Calendar, Clock, FileText, ExternalLink, Loader2 } from 'lucide-react';

/**
 * V1 Forms List Component - FROZEN
 *
 * This component displays forms for V1 events with custom naming support.
 * It is completely separate from the V2 EventFormsList component.
 *
 * DO NOT add V2 features to this component.
 * DO NOT mix V1/V2 logic.
 */
export const V1FormsList: React.FC = () => {
  const { eventSlug } = useEventContext();
  const { data: response, isLoading, error } = useV1EventForms(eventSlug);
  const { data: account } = useAccount();
  const { data: eventDetails } = useEventDetails(eventSlug);

  // Helper function to determine if text should be white or dark based on background color
  const shouldUseWhiteText = (hexColor: string): boolean => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance < 0.5;
  };

  // Helper function to darken a color for hover state
  const darkenColor = (hexColor: string, amount = 0.1): string => {
    const hex = hexColor.replace('#', '');
    const r = Math.max(0, parseInt(hex.substring(0, 2), 16) * (1 - amount));
    const g = Math.max(0, parseInt(hex.substring(2, 4), 16) * (1 - amount));
    const b = Math.max(0, parseInt(hex.substring(4, 6), 16) * (1 - amount));

    return `#${Math.round(r).toString(16).padStart(2, '0')}${Math.round(g).toString(16).padStart(2, '0')}${Math.round(b).toString(16).padStart(2, '0')}`;
  };

  // Helper function to lighten a color for gradient backgrounds
  const lightenColor = (hexColor: string, amount = 0.9): string => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);

    // Blend with white based on the amount (0 = original color, 1 = white)
    const newR = Math.min(255, Math.round(r + (255 - r) * amount));
    const newG = Math.min(255, Math.round(g + (255 - g) * amount));
    const newB = Math.min(255, Math.round(b + (255 - b) * amount));

    return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
  };

  // Helper function to create gradient colors from button color
  const getGradientColors = (buttonColor: string) => {
    // Create very light versions of the button color for the gradient
    const fromColor = lightenColor(buttonColor, 0.85); // Very light tint
    const toColor = lightenColor(buttonColor, 0.75);   // Slightly less light tint
    const iconColor = lightenColor(buttonColor, 0.3);  // Darker for icon visibility

    return {
      from: fromColor,
      to: toColor,
      icon: iconColor
    };
  };

  // Format date for display
  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A';
    const options: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Strip HTML tags and truncate description
  const stripHtmlAndTruncate = (html?: string, maxLength = 120) => {
    if (!html) return '';
    const temp = document.createElement('div');
    temp.innerHTML = html;
    const text = temp.textContent || temp.innerText || '';

    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  // Format question count text
  const formatQuestionCount = (count?: number) => {
    if (!count) return '0 questions';
    return count === 1 ? '1 question' : `${count} questions`;
  };

  if (isLoading) {
    return (
      <div className="min-h-[calc(100vh-64px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="text-center">
              <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading forms...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-[calc(100vh-64px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="text-center text-red-600">
              <p>Error loading forms. Please try again later.</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const forms = response?.forms || [];

  // Use custom naming from event details - same as navbar
  const formsName = eventDetails?.eventOptions?.formsNavName || 'Forms';
  // Proper pluralization for descriptive text
  const pluralFormsName = pluralize(formsName);

  if (forms.length === 0) {
    return (
      <div className="min-h-[calc(100vh-64px)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-6">
            <div className="text-center py-12">
              <FileText className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No {pluralFormsName} Available
              </h3>
              <p className="text-gray-500">
                There are no {pluralFormsName.toLowerCase()} available at this time.
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-64px)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white backdrop-blur-sm shadow-xl rounded-xl border border-gray-200/50 overflow-hidden">
          <div className="p-6 sm:p-8 space-y-6">
            {/* Header with custom naming */}
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold">{formsName}</h1>
              <p className="mt-2 text-gray-600">
                Browse and complete available {pluralFormsName.toLowerCase()}.
              </p>
            </div>

            {/* Form Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {forms.map((form) => (
                <div
                  key={form.id}
                  className="bg-gray-50 rounded-xl shadow-sm overflow-hidden hover:shadow-md transition-shadow duration-200 border border-gray-100"
                >
                  <div className="relative">
                    {form.logoUrl ? (
                      <div className="relative h-48 bg-gray-100 overflow-hidden">
                        {/* Blurred background image */}
                        <div
                          className="absolute inset-0 bg-cover bg-center blur-xl scale-110"
                          style={{
                            backgroundImage: `url(${form.logoUrl})`,
                          }}
                        />

                        {/* Overlay to soften the blurred background */}
                        <div className="absolute inset-0 bg-white/30" />

                        {/* Main image - maintains aspect ratio */}
                        <div className="relative h-full w-full flex items-center justify-center">
                          <img
                            src={form.logoUrl}
                            alt={form.name}
                            className="h-full w-full object-contain drop-shadow-lg"
                          />
                        </div>

                        {/* Subtle gradient overlay at bottom */}
                        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/50 to-transparent" />
                      </div>
                    ) : eventDetails?.imageUrl ? (
                      // Use event image as fallback
                      <div className="relative h-48 bg-gray-100 overflow-hidden">
                        {/* Blurred background image */}
                        <div
                          className="absolute inset-0 bg-cover bg-center blur-xl scale-110"
                          style={{
                            backgroundImage: `url(${eventDetails.imageUrl})`,
                          }}
                        />

                        {/* Overlay to soften the blurred background and add form color tint */}
                        <div
                          className="absolute inset-0 opacity-85 bg-white"
                        />

                        {/* Main image - maintains aspect ratio */}
                        <div className="relative h-full w-full flex items-center justify-center">
                          <img
                            src={eventDetails.imageUrl}
                            alt={eventDetails.name}
                            className="h-full w-full object-contain drop-shadow-lg opacity-90"
                          />
                        </div>

                        {/* Subtle gradient overlay at bottom */}
                        <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-white/50 to-transparent" />
                      </div>
                    ) : (
                      // Final fallback to icon with gradient
                      <div
                        className="w-full h-48 flex items-center justify-center"
                        style={{
                          background: (() => {
                            const gradientColors = getGradientColors(form.buttonColor);
                            return `linear-gradient(to bottom right, ${gradientColors.from}, ${gradientColors.to})`;
                          })()
                        }}
                      >
                        <FileText
                          className="h-16 w-16"
                          style={{
                            color: getGradientColors(form.buttonColor).icon
                          }}
                        />
                      </div>
                    )}

                    {form.requiresAccount && !account && (
                      <div className="absolute top-4 left-4">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          Login Required
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="p-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                      {form.name}
                    </h3>

                    <p className="text-gray-600 text-sm mb-4 min-h-[2.5rem] line-clamp-2">
                      {stripHtmlAndTruncate(form.description) || 'No description available'}
                    </p>

                    <div className="space-y-2 mb-4">
                      <div className="flex items-center text-sm text-gray-500">
                        <Calendar className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Opens: {formatDate(form.availability?.startsAt)}</span>
                      </div>

                      <div className="flex items-center text-sm text-gray-500">
                        <Clock className="h-4 w-4 mr-2 text-gray-400" />
                        <span>Closes: {formatDate(form.availability?.endsAt)}</span>
                      </div>

                      {form.surveyInfo && form.surveyInfo.totalQuestions > 0 && (
                        <div className="flex items-center text-sm text-gray-500">
                          <FileText className="h-4 w-4 mr-2 text-gray-400" />
                          <span>{formatQuestionCount(form.surveyInfo.totalQuestions)}</span>
                        </div>
                      )}
                    </div>

                    <div>
                      {form.availability?.isAvailable ? (
                        <Link
                          to={`/forms/${form.slug}`}
                          className="inline-flex items-center px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200"
                          style={{
                            backgroundColor: form.buttonColor,
                            color: shouldUseWhiteText(form.buttonColor) ? 'white' : '#1f2937',
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = darkenColor(form.buttonColor);
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = form.buttonColor;
                          }}
                        >
                          {form.buttonText}
                          <ExternalLink className="h-3.5 w-3.5 ml-1.5" />
                        </Link>
                      ) : (
                        <span className="inline-block px-4 py-2 bg-gray-100 text-gray-500 rounded-lg text-sm font-medium cursor-not-allowed">
                          Not Available
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};